// Responsive shell — UI/UX §5: bottom tabs < 768, collapsible sidebar 768–1024,
// persistent sidebar ≥ 1024, extra columns where §5.2/§5.3 call for them.
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Wordmark } from '../brand/index.js';
import {
  BookOpen,
  CalendarStar,
  House,
  type IconComponent,
  SidebarSimple,
  UserCircle,
  Users,
} from '../icons/index.js';
import { cx } from '../primitives/index.js';

export interface TabDef {
  key: string;
  label: string;
  icon: IconComponent;
  href: string;
  badge?: number | 'dot';
}

/** UI/UX §8.1 — the frozen five, with the icons the spec names. */
export const TABS: TabDef[] = [
  { key: 'home', label: 'Home', icon: House, href: '/' },
  { key: 'my-month', label: 'My Month', icon: CalendarStar, href: '/my-month' },
  { key: 'newsletter', label: 'Newsletter', icon: BookOpen, href: '/newsletter' },
  { key: 'families', label: 'Families', icon: Users, href: '/families' },
  { key: 'profile', label: 'Profile', icon: UserCircle, href: '/profile' },
];

/** §6 — badges bounce once when their count goes up while the app is open. */
function Badge({ badge }: { badge: number | 'dot' }) {
  const previous = useRef(badge);
  const [bouncing, setBouncing] = useState(false);
  useEffect(() => {
    const grew =
      typeof badge === 'number' && typeof previous.current === 'number'
        ? badge > previous.current
        : badge !== previous.current;
    previous.current = badge;
    if (!grew) return;
    setBouncing(true);
    const timer = setTimeout(() => setBouncing(false), 300);
    return () => clearTimeout(timer);
  }, [badge]);

  if (badge === 'dot') {
    return (
      <span
        className={cx(
          'absolute right-2 top-1 h-[6px] w-[6px] rounded-full bg-[var(--color-accent-primary)]',
          bouncing && 'roots-bounce',
        )}
      />
    );
  }
  if (badge <= 0) return null;
  return (
    <span
      className={cx(
        'type-label absolute right-1 top-0 min-w-[18px] rounded-full bg-[var(--color-accent-primary)] px-1 text-center text-[var(--color-on-accent)]',
        bouncing && 'roots-bounce',
      )}
    >
      {badge > 9 ? '9+' : badge}
    </span>
  );
}

function badgeLabel(tab: TabDef) {
  if (tab.badge === 'dot') return `${tab.label}, new`;
  if (typeof tab.badge === 'number' && tab.badge > 0) return `${tab.label}, ${tab.badge} new`;
  return tab.label;
}

const SIDEBAR_KEY = 'roots:sidebar-collapsed';

export function AppShell({
  tabs,
  activeKey,
  onNavigate,
  header,
  aside,
  children,
}: {
  tabs: TabDef[];
  activeKey: string;
  onNavigate: (href: string) => void;
  header?: ReactNode;
  /** Right-hand column on ≥1024 (reactions in the reader). */
  aside?: ReactNode;
  children: ReactNode;
}) {
  // §5.2 — the tablet sidebar collapses to an icon rail; §5.3 keeps it open on laptops.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      // Tablets start on the icon rail so two-column screens have room (§5.2).
      return localStorage.getItem(SIDEBAR_KEY) !== '0';
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
    } catch {}
  };

  return (
    <div className="min-h-dvh bg-[var(--color-bg-primary)]">
      {/* Top banner on every screen size: the terracotta wordmark, linking home (UI/UX §5). */}
      <header className="sticky top-0 z-40 border-b border-[var(--color-divider)] bg-[var(--color-bg-surface)] pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center gap-2 px-3 md:px-4">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className="hidden min-h-[44px] min-w-[44px] items-center justify-center rounded-[var(--radius-pill)] text-[var(--color-text-secondary)] md:flex lg:hidden"
          >
            <SidebarSimple size={22} />
          </button>
          <button
            type="button"
            onClick={() => onNavigate('/')}
            aria-label="Roots, go to Home"
            className="flex min-h-[44px] items-center rounded-[var(--radius-sm)] px-1"
          >
            <Wordmark title={null} className="h-8 w-auto text-[var(--color-accent-primary)]" />
          </button>
        </div>
        {header}
      </header>

      <div className="md:flex">
        <nav
          aria-label="Sections"
          className={cx(
            'sticky top-14 hidden h-[calc(100dvh-3.5rem)] shrink-0 flex-col gap-1 border-r border-[var(--color-divider)] p-3 md:flex lg:w-56 lg:p-4',
            collapsed ? 'md:w-[72px]' : 'md:w-56',
          )}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.key === activeKey;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => onNavigate(tab.href)}
                aria-current={active ? 'page' : undefined}
                aria-label={badgeLabel(tab)}
                className={cx(
                  'type-subheading relative flex min-h-[48px] items-center gap-3 rounded-[var(--radius-pill)] px-3 text-left',
                  active
                    ? 'bg-[var(--color-bg-surface)] text-[var(--color-accent-primary)]'
                    : 'text-[var(--color-text-secondary)]',
                )}
              >
                <Icon
                  size={24}
                  weight={active ? 'fill' : 'regular'}
                  aria-hidden
                  className="shrink-0"
                />
                <span className={cx(collapsed && 'md:sr-only lg:not-sr-only')}>{tab.label}</span>
                {tab.badge !== undefined && <Badge badge={tab.badge} />}
              </button>
            );
          })}
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mx-auto flex w-full max-w-[1200px] flex-1 gap-6 px-4 pb-28 pt-6 md:px-8 md:pb-8">
            {/* §6 — tab switch is a 150ms cross-fade; keyed so it only runs when the tab changes. */}
            <main key={activeKey} className="roots-crossfade min-w-0 flex-1">
              {children}
            </main>
            {aside && <aside className="hidden w-[320px] shrink-0 lg:block">{aside}</aside>}
          </div>
        </div>
      </div>

      <nav
        aria-label="Sections"
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-[var(--color-divider)] bg-[var(--color-bg-surface)] pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onNavigate(tab.href)}
              aria-current={active ? 'page' : undefined}
              aria-label={badgeLabel(tab)}
              className={cx(
                'relative flex min-h-[56px] min-w-[48px] flex-1 flex-col items-center justify-center gap-[2px] py-2',
                active
                  ? 'text-[var(--color-accent-primary)]'
                  : 'text-[var(--color-text-secondary)]',
              )}
            >
              <Icon size={24} weight={active ? 'fill' : 'regular'} aria-hidden />
              <span className="type-label normal-case tracking-normal">{tab.label}</span>
              {tab.badge !== undefined && <Badge badge={tab.badge} />}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

/** Tab titles are --type-heading (§2.2); --type-display is kept for editions and themes. */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <header className="flex w-full items-end justify-between gap-4">
      <div>
        <h1 className="type-heading">{title}</h1>
        {subtitle && <p className="type-caption mt-1">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

/**
 * §5.2 / §5.3 — list | detail. Below 768 only one pane shows (detail when present);
 * from 768 both sit side by side, each column's cards capped at 600px (560px on laptop).
 */
export function SplitView({
  list,
  detail,
  showDetailOnMobile,
  emptyDetail,
  wideDetail = false,
}: {
  list: ReactNode;
  detail?: ReactNode;
  showDetailOnMobile: boolean;
  /** Shown in the right column on ≥768 when nothing is selected. */
  emptyDetail?: ReactNode;
  /** Reader-style detail that takes the remaining width instead of the card cap. */
  wideDetail?: boolean;
}) {
  return (
    <div className="flex gap-6">
      <div
        className={cx(
          'min-w-0 flex-1 md:max-w-[600px] lg:max-w-[560px]',
          showDetailOnMobile && 'hidden md:block',
          wideDetail && 'md:w-[240px] md:flex-none lg:w-[280px]',
        )}
      >
        {list}
      </div>
      <div
        className={cx(
          'min-w-0 flex-1',
          !wideDetail && 'md:max-w-[600px] lg:max-w-[560px]',
          showDetailOnMobile ? 'block' : 'hidden md:block',
        )}
      >
        {detail ?? emptyDetail}
      </div>
    </div>
  );
}
